import { useCallback, useEffect, useState } from "react";
import * as admin from "@/lib/adminApi";

/**
 * The menu hub's data: every category (inactive ones included), their items with
 * variations, the menu groups and the master groups above them — plus the writes the server already supports.
 *
 * Every write answers `{ ok, error }` and reloads on success, so a screen never
 * shows a change the server did not accept (bible §44).
 */

// FastAPI 422s carry `detail` as a list of { loc, msg }; a toast would print
// "[object Object]". Name the field and say what is wrong with it instead.
export const errorText = failure => {
  const detail = failure?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map(entry => {
        const field = (entry.loc || []).filter(part => part !== "body").join(".");
        const msg = String(entry.msg || "is invalid").replace(/^Value error, /, "");
        return field ? `${field}: ${msg}` : msg;
      })
      .join("; ");
  }
  if (typeof detail === "string") return detail;
  return failure?.message || "Could not reach the server";
};

export function useMenuAdmin(token) {
  const [categories, setCategories] = useState(null);
  const [groups, setGroups] = useState(null);
  const [masters, setMasters] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [menu, groupList, masterList] = await Promise.all([
        admin.listMenuWithInactive(token),
        admin.listGroups(token),
        admin.listMasterGroups(token),
      ]);
      setCategories(menu);
      setGroups(groupList);
      setMasters(masterList);
      setError(null);
    } catch (failure) {
      setError(errorText(failure));
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(
    async call => {
      try {
        const data = await call();
        await load();
        return { ok: true, data };
      } catch (failure) {
        return { ok: false, error: errorText(failure) };
      }
    },
    [load]
  );

  // A move can renumber many rows; send them in order and reload once at the end
  // (also on failure, so the screen shows what the server actually holds).
  const reorder = useCallback(
    async (patches, put) => {
      try {
        for (const patch of patches) await put(patch.id, { sort_order: patch.sort_order }, token);
        return { ok: true };
      } catch (failure) {
        return { ok: false, error: errorText(failure) };
      } finally {
        await load();
      }
    },
    [load, token]
  );

  return {
    categories,
    groups,
    masters,
    error,
    reload: load,
    saveItem: (itemId, body) =>
      run(() => (itemId ? admin.updateMenuItem(itemId, body, token) : admin.createMenuItem(body, token))),
    createCategory: body => run(() => admin.createCategory(body, token)),
    updateCategory: (id, patch) => run(() => admin.updateCategory(id, patch, token)),
    deactivateCategory: id => run(() => admin.deactivateCategory(id, token)),
    createGroup: body => run(() => admin.createGroup(body, token)),
    updateGroup: (id, patch) => run(() => admin.updateGroup(id, patch, token)),
    deactivateGroup: id => run(() => admin.deactivateGroup(id, token)),
    reorderCategories: patches => reorder(patches, admin.updateCategory),
    reorderGroups: patches => reorder(patches, admin.updateGroup),
    createMaster: body => run(() => admin.createMasterGroup(body, token)),
    updateMaster: (id, patch) => run(() => admin.updateMasterGroup(id, patch, token)),
    deactivateMaster: id => run(() => admin.deactivateMasterGroup(id, token)),
    reorderMasters: patches => reorder(patches, admin.updateMasterGroup),
    createVariation: (itemId, body) => run(() => admin.createVariation(itemId, body, token)),
    updateVariation: (itemId, id, patch) => run(() => admin.updateVariation(itemId, id, patch, token)),
    deleteVariation: (itemId, id) => run(() => admin.deleteVariation(itemId, id, token)),
  };
}
